import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { latestDealRecordQueryOptions } from '@lib/query/options/payment';
import { getFirstDayOfCurrentMonthDate } from '@lib/util/date';
import { convertToTwd } from '@lib/util/money';
import Tag from '@modules/common/components/tag';
import Text from '@modules/common/components/text';

/**
 * Format date to display format (MM月)
 */
const formatMonthDisplay = (date: Date): string => {
  return String(date.getMonth() + 1).padStart(2, '0');
};

/**
 * Format date to display format (YYYY-MM-DD)
 */
const formatDateDisplay = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DepositConfirm = () => {
  const firstDayOfCurrentMonth = useMemo(
    () => getFirstDayOfCurrentMonthDate(),
    []
  );

  const { data: dealRecord } = useQuery(
    latestDealRecordQueryOptions(
      firstDayOfCurrentMonth.toISOString()
    )
  );

  const totalBalance = dealRecord
    ? (dealRecord.cashBalance ?? 0) +
      (dealRecord.rewardBalance ?? 0) +
      (dealRecord.refundBalance ?? 0)
    : 0;

  return (
    <div data-testid="confirm-modal-deposit-container">
      <Text variant="title-bold" as="h3">
        {formatMonthDisplay(firstDayOfCurrentMonth)} 月份儲值金餘額確認通知
      </Text>
      <Text variant="sm-regular" as="div" className="text-grey-10 mb-2 mt-2">
        {formatDateDisplay(firstDayOfCurrentMonth)}
      </Text>
      <Tag
        text="儲值金"
        className="bg-orange-30_op10 text-orange-30 rounded-full px-4 py-2 font-normal"
      />
      <div className="text-black-20 mt-6 flex flex-col gap-4">
        <p>
          親愛的用戶，
          <br />
          您的帳戶儲值金餘額截至上個月月底為
          <Text variant="h3-bold" as="span" className="mx-1">{convertToTwd(totalBalance)}</Text>
          <br />
          <span className="text-grey-60">
            = 儲值金 {convertToTwd(dealRecord?.cashBalance ?? 0)} + 回饋金
            {convertToTwd(dealRecord?.rewardBalance ?? 0)} + 退貨回饋金
            {convertToTwd(dealRecord?.refundBalance ?? 0)}
          </span>
        </p>
        <p>
          為了不影響您的的購物體驗，請確認餘額是否充足。
          <br />
          如需檢視詳細明細，您可以點擊
          <a
            href="/account/marx-payment-account"
            className="text-blue-30 mx-1 cursor-pointer font-bold hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            儲值金紀錄頁面
          </a>
          查看。
        </p>
        <p>
          系統將在每個月一號發送儲值金確認通知,
          <br />
          為保障您的儲值金權益，請每月確認一次您的儲值金餘額。
          <br />
          若超過一個月未進行確認，系統將暫停接受新訂單。
          <br />
          請在確認餘額無誤後點擊「我已閱讀並同意」按鈕，
          <br />
          感謝您的配合 !
        </p>
        <p>如有任何疑問或需要協助，請洽您的業務專員或是聯繫客服。</p>
        <ul className="text-grey-60 list-disc pl-6">
          <li>客服郵箱：marxpath@marxbiotech.com</li>
          <li>客服電話：02-82455250（服務時間：週一至週五，9:00至18:00）</li>
        </ul>
      </div>
    </div>
  );
};

export default DepositConfirm;
